import React, { useState, useMemo } from 'react';
import { Motor, Battery, Propeller, SystemConfig, AnalysisResult, SortCriteria } from '../types';
import { Button, ButtonDivider, Toolbar, ToolbarGroup } from './ButtonGroup';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface AnalysisResultsTabProps {
  motors: Motor[];
  batteries: Battery[];
  propellers: Propeller[];
  config: SystemConfig;
  analysisResults: AnalysisResult[];
  onAnalysisResultsChange: (results: AnalysisResult[]) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const AnalysisResultsTab: React.FC<AnalysisResultsTabProps> = ({ 
  motors, 
  batteries, 
  propellers, 
  config, 
  analysisResults, 
  onAnalysisResultsChange, 
  showStatus 
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [sortCriteria, setSortCriteria] = useState<SortCriteria>('score');
  const [showSummary, setShowSummary] = useState(false);

  // 计算当前选中的组合
  const calculateSelectedCombinations = () => {
    const results: AnalysisResult[] = [];
    let combinationIndex = 1;

    const { targetWeight, otherPower, bladeCount } = config;
    const motorCount = 2;

    // 筛选适合的桨叶（基于选择的桨叶数量）
    const filteredPropellers = propellers.filter(prop => prop.blades === bladeCount);

    if (filteredPropellers.length === 0) {
      showStatus(t('analysis.messages.noPropellerData', { bladeCount }), 'warning');
      return;
    }

    // 只使用被标记为参与分析的组件
    const analysisMotors = motors.filter(m => m.includeInAnalysis);
    const analysisBatteries = batteries.filter(b => b.includeInAnalysis);
    const analysisPropellers = filteredPropellers.filter(p => p.includeInAnalysis);

    if (analysisMotors.length === 0 || analysisBatteries.length === 0 || analysisPropellers.length === 0) {
      showStatus(t('analysis.messages.selectComponents'), 'warning');
      return;
    }

    analysisMotors.forEach(motor => {
      analysisBatteries.forEach(battery => {
        analysisPropellers.forEach(prop => {
          // 计算性能参数
          const totalThrust = motor.maxThrust * motorCount;
          const thrustMargin = ((totalThrust / (targetWeight * 1000)) - 1) * 100;

          // 更精确的功率计算
          const motorEfficiencyAtHover = 0.75; // 悬停时电机效率
          const hoverThrust = (targetWeight * 1000) / motorCount; // 单电机悬停推力
          const thrustRatio = hoverThrust / motor.maxThrust; // 推力使用比例

          // 基于推力比例计算悬停功率
          const hoverPowerPerMotor = motor.maxPower * Math.pow(thrustRatio, 1.5) / motorEfficiencyAtHover;
          const totalMotorPower = hoverPowerPerMotor * motorCount;
          const systemPower = totalMotorPower + otherPower;

          // 计算续航时间
          const endurance = (battery.energy / systemPower) * 60; // 分钟

          // 计算总重量
          const totalWeight = (motor.weight * motorCount) + battery.weight + (prop.weight * motorCount) + 350; // +350g机架及其他

          // 系统效率指标
          const thrustToWeightRatio = totalThrust / totalWeight;
          const powerEfficiency = totalThrust / systemPower; // g/W
          const efficiency = thrustToWeightRatio * powerEfficiency / 10;

          // ESC规格建议
          const escCurrent = Math.ceil(motor.maxCurrent * thrustRatio * 1.3); // 30%安全余量
          const escSpec = escCurrent + 'A';

          // 总成本
          const totalCost = (motor.price * motorCount) + battery.price + (prop.price * motorCount) + 300; // +300其他组件

          // 综合评分算法
          let score = 0;

          // 续航时间评分 (0-30分)
          if (endurance >= 30) score += 30;
          else if (endurance >= 25) score += 25 + (endurance - 25);
          else if (endurance >= 18) score += 18 + (endurance - 18) * 0.8;
          else score += endurance * 0.6;

          // 推力冗余评分 (0-25分)
          if (thrustMargin >= 100) score += 25;
          else if (thrustMargin >= 80) score += 22 + (thrustMargin - 80) * 0.15;
          else if (thrustMargin >= 50) score += 15 + (thrustMargin - 50) * 0.23;
          else score += thrustMargin * 0.3;

          // 重量控制评分 (0-20分)
          if (totalWeight <= 1200) score += 20;
          else if (totalWeight <= 1400) score += 15 + (1400 - totalWeight) * 0.025;
          else if (totalWeight <= 1600) score += 10 + (1600 - totalWeight) * 0.025;
          else score += Math.max(0, 5 - (totalWeight - 1600) * 0.01);

          // 系统效率评分 (0-15分)
          if (efficiency >= 2.0) score += 15;
          else if (efficiency >= 1.5) score += 10 + (efficiency - 1.5) * 10;
          else score += efficiency * 6.67;

          // 成本控制评分 (0-10分)
          if (totalCost <= 2000) score += 10;
          else if (totalCost <= 3000) score += 7 + (3000 - totalCost) * 0.003;
          else score += Math.max(0, 5 - (totalCost - 3000) * 0.001);

          results.push({
            index: combinationIndex++,
            motor: motor.brand + ' ' + motor.model,
            battery: battery.brand + ' ' + battery.model,
            propeller: prop.spec + ' (' + prop.blades + '叶)',
            totalThrust: Math.round(totalThrust),
            thrustMargin: thrustMargin.toFixed(1),
            totalWeight: Math.round(totalWeight),
            systemPower: Math.round(systemPower),
            endurance: endurance.toFixed(1),
            efficiency: efficiency.toFixed(2),
            escSpec: escSpec,
            totalCost: Math.round(totalCost),
            score: score.toFixed(1),
            // 存储原始数据用于排序
            _thrustMargin: thrustMargin,
            _endurance: endurance,
            _efficiency: efficiency,
            _totalWeight: totalWeight,
            _totalCost: totalCost,
            _score: score
          });
        });
      });
    });

    // 按综合评分排序
    results.sort((a, b) => b._score - a._score);

    onAnalysisResultsChange(results);
    setShowSummary(true);
    showStatus(t('analysis.messages.calculatedSelected', { count: results.length }));
  };

  // 计算所有组合（不考虑选中状态）
  const calculateAllCombinations = () => {
    const results: AnalysisResult[] = [];
    let combinationIndex = 1;

    const { targetWeight, otherPower, bladeCount } = config;
    const motorCount = 2;

    // 筛选适合的桨叶（基于选择的桨叶数量）
    const filteredPropellers = propellers.filter(prop => prop.blades === bladeCount);

    if (filteredPropellers.length === 0) {
      showStatus(t('analysis.messages.noPropellerData', { bladeCount }), 'warning');
      return;
    }

    // 使用所有组件，不考虑是否被标记为参与分析
    const analysisMotors = motors;
    const analysisBatteries = batteries;
    const analysisPropellers = filteredPropellers;

    if (analysisMotors.length === 0 || analysisBatteries.length === 0 || analysisPropellers.length === 0) {
      showStatus(t('analysis.messages.insufficientData'), 'warning');
      return;
    }

    analysisMotors.forEach(motor => {
      analysisBatteries.forEach(battery => {
        analysisPropellers.forEach(prop => {
          // 计算性能参数
          const totalThrust = motor.maxThrust * motorCount;
          const thrustMargin = ((totalThrust / (targetWeight * 1000)) - 1) * 100;

          // 更精确的功率计算
          const motorEfficiencyAtHover = 0.75; // 悬停时电机效率
          const hoverThrust = (targetWeight * 1000) / motorCount; // 单电机悬停推力
          const thrustRatio = hoverThrust / motor.maxThrust; // 推力使用比例

          // 基于推力比例计算悬停功率
          const hoverPowerPerMotor = motor.maxPower * Math.pow(thrustRatio, 1.5) / motorEfficiencyAtHover;
          const totalMotorPower = hoverPowerPerMotor * motorCount;
          const systemPower = totalMotorPower + otherPower;

          // 计算续航时间
          const endurance = (battery.energy / systemPower) * 60; // 分钟

          // 计算总重量
          const totalWeight = (motor.weight * motorCount) + battery.weight + (prop.weight * motorCount) + 350; // +350g机架及其他

          // 系统效率指标
          const thrustToWeightRatio = totalThrust / totalWeight;
          const powerEfficiency = totalThrust / systemPower; // g/W
          const efficiency = thrustToWeightRatio * powerEfficiency / 10;

          // ESC规格建议
          const escCurrent = Math.ceil(motor.maxCurrent * thrustRatio * 1.3); // 30%安全余量
          const escSpec = escCurrent + 'A';

          // 总成本
          const totalCost = (motor.price * motorCount) + battery.price + (prop.price * motorCount) + 300; // +300其他组件

          // 综合评分算法
          let score = 0;

          // 续航时间评分 (0-30分)
          if (endurance >= 30) score += 30;
          else if (endurance >= 25) score += 25 + (endurance - 25);
          else if (endurance >= 18) score += 18 + (endurance - 18) * 0.8;
          else score += endurance * 0.6;

          // 推力冗余评分 (0-25分)
          if (thrustMargin >= 100) score += 25;
          else if (thrustMargin >= 80) score += 22 + (thrustMargin - 80) * 0.15;
          else if (thrustMargin >= 50) score += 15 + (thrustMargin - 50) * 0.23;
          else score += thrustMargin * 0.3;

          // 重量控制评分 (0-20分)
          if (totalWeight <= 1200) score += 20;
          else if (totalWeight <= 1400) score += 15 + (1400 - totalWeight) * 0.025;
          else if (totalWeight <= 1600) score += 10 + (1600 - totalWeight) * 0.025;
          else score += Math.max(0, 5 - (totalWeight - 1600) * 0.01);

          // 系统效率评分 (0-15分)
          if (efficiency >= 2.0) score += 15;
          else if (efficiency >= 1.5) score += 10 + (efficiency - 1.5) * 10;
          else score += efficiency * 6.67;

          // 成本控制评分 (0-10分)
          if (totalCost <= 2000) score += 10;
          else if (totalCost <= 3000) score += 7 + (3000 - totalCost) * 0.003;
          else score += Math.max(0, 5 - (totalCost - 3000) * 0.001);

          results.push({
            index: combinationIndex++,
            motor: motor.brand + ' ' + motor.model,
            battery: battery.brand + ' ' + battery.model,
            propeller: prop.spec + ' (' + prop.blades + '叶)',
            totalThrust: Math.round(totalThrust),
            thrustMargin: thrustMargin.toFixed(1),
            totalWeight: Math.round(totalWeight),
            systemPower: Math.round(systemPower),
            endurance: endurance.toFixed(1),
            efficiency: efficiency.toFixed(2),
            escSpec: escSpec,
            totalCost: Math.round(totalCost),
            score: score.toFixed(1),
            // 存储原始数据用于排序
            _thrustMargin: thrustMargin,
            _endurance: endurance,
            _efficiency: efficiency,
            _totalWeight: totalWeight,
            _totalCost: totalCost,
            _score: score
          });
        });
      });
    });

    // 按综合评分排序
    results.sort((a, b) => b._score - a._score);

    onAnalysisResultsChange(results);
    setShowSummary(true);
    showStatus(t('analysis.messages.calculatedAll', { count: results.length }));
  };

  // 排序分析结果
  const sortResults = (criteria: SortCriteria) => {
    const sorted = [...analysisResults];

    switch (criteria) {
      case 'endurance':
        sorted.sort((a, b) => b._endurance - a._endurance);
        break;
      case 'efficiency':
        sorted.sort((a, b) => b._efficiency - a._efficiency);
        break;
      case 'thrust-margin':
        sorted.sort((a, b) => b._thrustMargin - a._thrustMargin);
        break;
      case 'cost':
        sorted.sort((a, b) => a._totalCost - b._totalCost);
        break;
      default:
        sorted.sort((a, b) => b._score - a._score);
    }

    onAnalysisResultsChange(sorted);
    setSortCriteria(criteria);
  };

  // 高亮最优方案
  const highlightBestOptions = () => {
    showStatus(t('analysis.messages.highlightedTop5'));
  };

  // 计算分析概览数据
  const analysisStatistics = useMemo(() => {
    if (analysisResults.length === 0) return null;

    const bestEndurance = Math.max(...analysisResults.map(r => r._endurance));
    const bestThrustMargin = Math.max(...analysisResults.map(r => r._thrustMargin));
    const lowestWeight = Math.min(...analysisResults.map(r => r._totalWeight));
    const lowestCost = Math.min(...analysisResults.map(r => r._totalCost));

    return {
      totalCombinations: analysisResults.length,
      bestEndurance: bestEndurance.toFixed(1) + ' 分钟',
      bestThrustMargin: bestThrustMargin.toFixed(1) + '%',
      lowestWeight: Math.round(lowestWeight) + 'g',
      lowestCost: '¥' + Math.round(lowestCost)
    };
  }, [analysisResults]);

  return (
    <div className="space-y-6">
      <div>
        <h2 
          className="text-lg font-semibold mb-4 pb-2 border-b"
          style={{ 
            color: colors.text,
            borderColor: colors.borderLight 
          }}
        >
          {t('analysis.title')}
        </h2>

        {/* 工具栏 */}
        <Toolbar>
          <ToolbarGroup title={t('analysis.analysisCalculation')}>
            <Button onClick={calculateSelectedCombinations} variant="primary">
              {t('analysis.calculateSelected')}
            </Button>
            <Button onClick={calculateAllCombinations} variant="secondary">
              {t('analysis.calculateAll')}
            </Button>
            <Button onClick={highlightBestOptions} variant="success">
              {t('analysis.highlightBest')}
            </Button>
          </ToolbarGroup>
          
          <ButtonDivider />
          
          <ToolbarGroup title={t('analysis.sortBy')}>
            <div className="flex items-center gap-2">
              <label 
                htmlFor="sort-criteria" 
                className="text-sm font-medium"
                style={{ color: colors.textSecondary }}
              >
                {t('analysis.sortBy')}:
              </label>
              <select
                id="sort-criteria"
                value={sortCriteria}
                onChange={(e) => sortResults(e.target.value as SortCriteria)}
                className="px-3 py-1 border rounded-md text-sm focus:outline-none"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                <option value="score">{t('analysis.sortOptions.score')}</option>
                <option value="endurance">{t('analysis.sortOptions.endurance')}</option>
                <option value="efficiency">{t('analysis.sortOptions.efficiency')}</option>
                <option value="thrust-margin">{t('analysis.sortOptions.thrustMargin')}</option>
                <option value="cost">{t('analysis.sortOptions.cost')}</option>
              </select>
            </div>
          </ToolbarGroup>
        </Toolbar>

        {/* 分析概览 */}
        {showSummary && analysisStatistics && (
          <div 
            className="rounded-lg border p-4 mb-6"
            style={{ 
              backgroundColor: colors.surface,
              borderColor: colors.border 
            }}
          >
            <h3 
              className="text-base font-semibold mb-3"
              style={{ color: colors.text }}
            >
              {t('analysis.overview')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div 
                className="rounded-lg border p-3 text-center"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight 
                }}
              >
                <div className="text-xs mb-1" style={{ color: colors.textMuted }}>{t('analysis.stats.totalCombinations')}</div>
                <div className="text-base font-semibold" style={{ color: colors.text }}>{analysisStatistics.totalCombinations}</div>
              </div>
              <div 
                className="rounded-lg border p-3 text-center"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight 
                }}
              >
                <div className="text-xs mb-1" style={{ color: colors.textMuted }}>{t('analysis.stats.bestEndurance')}</div>
                <div className="text-base font-semibold" style={{ color: colors.text }}>{analysisStatistics.bestEndurance}</div>
              </div>
              <div 
                className="rounded-lg border p-3 text-center"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight 
                }}
              >
                <div className="text-xs mb-1" style={{ color: colors.textMuted }}>{t('analysis.stats.bestThrustMargin')}</div>
                <div className="text-base font-semibold" style={{ color: colors.text }}>{analysisStatistics.bestThrustMargin}</div>
              </div>
              <div 
                className="rounded-lg border p-3 text-center"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight 
                }}
              >
                <div className="text-xs mb-1" style={{ color: colors.textMuted }}>{t('analysis.stats.lowestWeight')}</div>
                <div className="text-base font-semibold" style={{ color: colors.text }}>{analysisStatistics.lowestWeight}</div>
              </div>
              <div 
                className="rounded-lg border p-3 text-center"
                style={{ 
                  backgroundColor: colors.background,
                  borderColor: colors.borderLight 
                }}
              >
                <div className="text-xs mb-1" style={{ color: colors.textMuted }}>{t('analysis.stats.lowestCost')}</div>
                <div className="text-base font-semibold" style={{ color: colors.text }}>{analysisStatistics.lowestCost}</div>
              </div>
            </div>
          </div>
        )}

        {/* 分析结果表格 */}
        <div 
          className="rounded-lg border overflow-hidden"
          style={{ 
            backgroundColor: colors.background,
            borderColor: colors.border 
          }}
        >
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead 
                className="sticky top-0 z-10"
                style={{ 
                  backgroundColor: colors.surface,
                  borderBottom: `1px solid ${colors.border}`
                }}
              >
                <tr>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.rank')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.motor')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.battery')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.propeller')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.totalThrust')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.thrustMargin')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.totalWeight')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.systemPower')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.endurance')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.efficiency')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.escSpec')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.totalCost')}
                  </th>
                  <th 
                    className="px-3 py-2 text-left font-semibold"
                    style={{ 
                      color: colors.text
                    }}
                  >
                    {t('analysis.headers.totalScore')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {analysisResults.length > 0 ? (
                  analysisResults.map((result, index) => {
                    const isTopRank = index < 5;
                    const isExcellentEndurance = parseFloat(result.endurance) >= 25;
                    const isTopScore = index < 3;
                    
                    return (
                      <tr 
                        key={result.index} 
                        className="border-b transition-colors duration-200"
                        style={{
                          backgroundColor: isTopRank 
                            ? (colors.accent + '10') 
                            : isExcellentEndurance 
                              ? (colors.success + '10')
                              : colors.background,
                          borderColor: colors.borderLight,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = colors.surfaceHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isTopRank 
                            ? (colors.accent + '10') 
                            : isExcellentEndurance 
                              ? (colors.success + '10')
                              : colors.background;
                        }}
                      >
                        <td className="px-3 py-2" style={{ color: colors.text }}>{index + 1}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.motor}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.battery}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.propeller}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.totalThrust}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.thrustMargin}%</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.totalWeight}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.systemPower}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.endurance}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.efficiency}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>{result.escSpec}</td>
                        <td className="px-3 py-2" style={{ color: colors.text }}>¥{result.totalCost}</td>
                        <td 
                          className="px-3 py-2"
                          style={{ 
                            color: isTopScore ? colors.accent : colors.text,
                            fontWeight: isTopScore ? 'bold' : 'normal',
                            backgroundColor: isTopScore ? (colors.accent + '20') : 'transparent'
                          }}
                        >
                          {result.score}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td 
                      colSpan={13} 
                      className="px-3 py-8 text-center"
                      style={{ color: colors.textMuted }}
                    >
                      {t('analysis.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisResultsTab; 